import React, { useRef } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import { Navigation, Scrollbar } from 'swiper';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  maxBreakpointQuery,
  sectionMargins,
  fontSize,
  brandColours,
} from '../styles';
import {
  Container,
  Video,
  Heading,
  HtmlContent,
  Button,
  Slider,
  PrevButton,
  NextButton,
} from './ui';

const StyledMediaContent = styled.section`
  ${({ isTabs }) => {
    if (isTabs) {
      return css`
        ${sectionMargins('20px', '40px')};
      `;
    } else {
      return css`
        ${sectionMargins()};
      `;
    }
  }}
`;

const StyledInner = styled.div`
  display: grid;
  gap: 30px;

  ${minBreakpointQuery.smedium`
    grid-template-columns: repeat(2, 1fr);
    align-items: center;
    gap: 40px;
  `}

  ${minBreakpointQuery.medium`
    gap: 60px;
  `}

  ${({ isTabs }) => {
    if (!isTabs) {
      return css`
        ${minBreakpointQuery.mlarge`
          gap: 80px;
        `}

        ${minBreakpointQuery.large`
          gap: 100px;
        `}
      `;
    }
  }}

  ${({ version }) => {
    if (version === 2) {
      return css`
        ${minBreakpointQuery.xlarge`
          gap: 120px;
        `}

        ${minBreakpointQuery.xxlarge`
          gap: 150px;
        `}

        ${minBreakpointQuery.xxxlarge`
          gap: 180px;
        `}

        ${minBreakpointQuery.xxxxlarge`
          gap: 210px;
        `}
      `;
    }
  }}
`;

const StyledMedia = styled.div`
  overflow: hidden;

  ${({ flip }) => {
    if (flip) {
      return css`
        ${minBreakpointQuery.smedium`
          grid-row: 1;
          grid-column: 2;
        `}
      `;
    }
  }}

  ${({ version }) => {
    if (version === 2) {
      return css`
        ${({ flip }) => (flip ? 'margin-right: -30px;' : 'margin-left: -30px;')}

        ${maxBreakpointQuery.smedium`
          ${({ flip }) =>
            flip ? 'margin-left: -30px;' : 'margin-right: -30px;'}
        `}

        ${minBreakpointQuery.smedium`
          height: 100%;
        `}
      `;
    }
  }}
`;

const StyledSliderWrapper = styled.div`
  position: relative;
  padding-bottom: 50px;
`;

const StyledPrevButton = styled(PrevButton)`
  position: absolute;
  top: 50%;
  left: 10px;
  height: 50px;
  width: 50px;
`;

const StyledNextButton = styled(NextButton)`
  position: absolute;
  top: 50%;
  right: 10px;
  height: 50px;
  width: 50px;
`;

const StyledSlider = styled(Slider)`
  position: static;
  width: 100%;

  .swiper-slide {
    height: auto;
    width: 100%;
  }

  .swiper-scrollbar {
    bottom: 10px;
    left: 50%;
    height: 5px;
    max-width: 280px;
    transform: translateX(-50%);

    .swiper-scrollbar-drag {
      background-color: ${brandColours.primary};
    }
  }
`;

const StyledItem = styled.div`
  display: flex;
  height: 100%;
`;

const StyledImage = styled(GatsbyImage)`
  ${({ width }) => {
    if (width) {
      return css`
        display: block;
        margin: auto;
        max-width: ${width}px;
      `;
    }
  }}

  ${({ version }) => {
    if (version !== 3) {
      return css`
        ${maxBreakpointQuery.smedium`
          aspect-ratio: 2/1;

          @supports not (aspect-ratio: 3/2) {
            max-height: 430px;
          }
        `}

        ${maxBreakpointQuery.small`
          aspect-ratio: 3/2;
        `}

        ${maxBreakpointQuery.tsmall`
          aspect-ratio: 4/3;
        `}

        ${minBreakpointQuery.smedium`
          height: 100%;
        `}
      `;
    }
  }}
`;

const StyledVideo = styled(Video)`
  height: 100%;
`;

const StyledContent = styled.div`
  ${minBreakpointQuery.smedium`
    margin-top: 30px;
    margin-bottom: 30px;
  `}

  ${({ version }) => {
    if (version === 2) {
      return css`
        ${minBreakpointQuery.smedium`
          ${({ flip }) => (flip ? 'margin-left: auto;' : 'margin-right: auto;')}
          max-width: 520px;
        `}

        ${minBreakpointQuery.mlarge`
          margin-top: 40px;
          margin-bottom: 40px;
        `}

        ${minBreakpointQuery.large`
          margin-top: 60px;
          margin-bottom: 60px;
        `}

        ${minBreakpointQuery.xlarge`
          margin-top: 80px;
          margin-bottom: 80px;
        `}

        ${minBreakpointQuery.xxlarge`
          margin-top: 100px;
          margin-bottom: 100px;
        `}

        ${minBreakpointQuery.xxxlarge`
          margin-top: 120px;
          margin-bottom: 120px;
        `}

        ${minBreakpointQuery.xxxxlarge`
          margin-top: 140px;
          margin-bottom: 140px;
        `}
      `;
    }
  }}
`;

const StyledHeading = styled(Heading)`
  ${({ as }) => {
    if (as === 'h4') {
      return css`
        ${fontSize(18)};

        ${minBreakpointQuery.small`
          ${fontSize(19)};
        `}

        ${minBreakpointQuery.large`
          ${fontSize(20)};
        `}
      `;
    }
  }}
`;

const StyledText = styled(HtmlContent)`
  margin-top: 20px;

  ${minBreakpointQuery.small`
    margin-top: 25px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 30px;
  `}
`;

const StyledButton = styled(Button)`
  margin-top: 20px;

  ${minBreakpointQuery.small`
    margin-top: 25px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 30px;
  `}
`;

const MediaContent = ({
  media,
  autoplayVideo,
  heading,
  text,
  link,
  flip,
  version,
  isTabs,
}) => {
  const sliderRef = useRef();

  const sliderOptions = {
    modules: [Navigation, Scrollbar],
    slidesPerView: 1,
    spaceBetween: 10,
    loop: true,
    scrollbar: { draggable: true },
    onBeforeInit: swiper => {
      sliderRef.current = swiper;
    },
  };

  return (
    <StyledMediaContent as={isTabs && 'div'} isTabs={isTabs}>
      <Container narrow={isTabs} wide={version === 2}>
        <StyledInner version={version} isTabs={isTabs}>
          <StyledMedia flip={flip} version={version}>
            {media.length > 1 ? (
              <StyledSliderWrapper>
                <StyledSlider ref={sliderRef} {...sliderOptions}>
                  {media.map((image, id) => (
                    <StyledItem key={id}>
                      <StyledImage
                        image={image.gatsbyImageData}
                        alt={image.alt}
                        width={image.width}
                        version={version}
                      />
                    </StyledItem>
                  ))}
                </StyledSlider>
                <StyledPrevButton
                  onClick={() => sliderRef.current?.slidePrev()}
                />
                <StyledNextButton
                  onClick={() => sliderRef.current?.slideNext()}
                />
              </StyledSliderWrapper>
            ) : media[0] && media[0].isImage ? (
              <StyledImage
                image={media[0].gatsbyImageData}
                alt={media[0].alt}
                width={media[0].width}
                version={version}
              />
            ) : (
              <StyledVideo
                data={media}
                controls={!autoplayVideo}
                autoPlay={autoplayVideo}
                isMediaContent={true}
              />
            )}
          </StyledMedia>
          <StyledContent flip={flip} version={version}>
            <StyledHeading as={isTabs && 'h4'}>{heading}</StyledHeading>
            <StyledText content={text} />
            {link && <StyledButton to={link.pageUrl}>{link.text}</StyledButton>}
          </StyledContent>
        </StyledInner>
      </Container>
    </StyledMediaContent>
  );
};

export default MediaContent;
